import React from 'react'

import {
  useAccount,
  useActions,
  useAppcues,
  useDebounce,
  useIsAddressValid,
  useMeasurements,
  useMemo,
  useNavigate,
  useQueries,
  useState,
} from 'src/hooks'
import { report } from 'src/utils/error'
import { ACCOUNT_TOOLS_ID, RP_TOOLS_IDS } from 'src/app/constants'
import {
  CampaignFragment,
  getItemCategories,
  ItemCategoryFragment,
  ItemFragment,
  PlanFragment,
} from 'src/legacy_graphql'
import {
  DefaultError,
  Drawer,
  FullWindowModal,
  InstantSearch,
  SuspenseBoundary,
  Transition,
} from 'src/chrome'
import Plans from 'src/plans/containers/Plans'
import PlanBuyer from 'src/plan-buyer/containers/PlanBuyer'

import {
  Banner,
  CampaignList,
  Item as ItemComponent,
  PaymentModal,
  ThanksModal,
  TileRibbon,
} from '../components'
import SearchResults from './SearchResults'
import styles from './campaignStore.module.scss'
import { CampaignStoreRoute } from '../routes/CampaignStoreRoute'
import { Item } from '../types'
import CircularProgress from '@material-ui/core/CircularProgress'
import { ValidateAddress } from 'src/payments/components'

const upsaleMessage = {
  title: "Sorry, you can't purchase campaigns on a basic subscription",
  message:
    'Campaigns are an amazing and easy way to help your customers feel appreciation. Campaigns can be used to set a sequence of cards to send based on time delays. Never forget to send an amazing follow-up card again and upgrade your account today!',
}

interface Props {
  route: CampaignStoreRoute
}

enum Steps {
  Idle,
  Preview,
  ChoosePlan,
  BuyPlan,
  BuyItem,
  Thanks,
  FixAddress,
}

type ItemType = 'campaign' | 'product'

interface ItemBuy {
  itemType: ItemType
  item: ItemFragment
  plan: PlanFragment
}

type CampaignBuy = ItemBuy & {
  itemType: 'campaign'
  item: ItemFragment & { campaign: CampaignFragment }
}

type ProductBuy = ItemBuy & {
  itemType: 'product'
  item: ItemFragment & { campaign: null }
}

type IdleStep = { type: Steps.Idle }
type PreviewStep = { type: Steps.Preview; itemId: string }
type ChoosePlanStep = { type: Steps.ChoosePlan; itemId: string }
type ThanksStep = { type: Steps.Thanks }
type FixAddressStep = { type: Steps.FixAddress; itemId: string }
type BuyPlanStep = { type: Steps.BuyPlan; itemId: string; planId: string }
type BuyItemStep = {
  type: Steps.BuyItem
} & (CampaignBuy | ProductBuy)

type Step =
  | IdleStep
  | PreviewStep
  | ChoosePlanStep
  | BuyItemStep
  | BuyPlanStep
  | ThanksStep
  | FixAddressStep

const featuredProducts: (Item & { startColor: string; endColor: string })[] = [
  {
    __typename: 'Item',
    id: '562',
    campaign: null,
    itemCode: '13704',
    name: 'The Essentials Pack',
    description: [
      'The essentials pack includes a personalized handwriting font,',
      'four signatures, and 6 beautifully designed first impressions cards',
    ].join(' '),
    price: '$99',
    image:
      'https://www.sendoutcards.com/soc-media/images/EssentialsPack.original.jpg',
    startColor: 'rgb(255, 219, 48)',
    endColor: 'rgb(244, 140, 67)',
  },
]

const CampaignStoreContent: React.FC<Props> = props => {
  const {
    route: { categoryId },
  } = props

  const invalidateQueriesByName = useActions().invalidatedQueriesByName
  const { CampaignStoreIndex, CampaignStoreCategory, push } = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 500)
  const [action, setStep] = useState<Step>({ type: Steps.Idle })
  const account = useAccount()
  const { windowWidth } = useMeasurements()
  const isAddressValid = useIsAddressValid()
  const [hasValidatedAddress, setHasValidatedAddress] = useState(false)

  const endStep = () => setStep({ type: Steps.Idle })

  const [allCategories] = useQueries(getItemCategories())

  const selectedCategory: ItemCategoryFragment | undefined = useMemo(() => {
    if (categoryId) {
      const category = allCategories.find(x => x.id === categoryId)
      if (category) {
        return category
      }

      report(new Error(`campaign category ${categoryId} not found`))
      push(CampaignStoreIndex, {})
    }

    return undefined
  }, [allCategories, categoryId, CampaignStoreIndex, push])

  /* An array of all the categories appearing on the page. If a category is
     selected display only that, otherwise show non-emptyish ones. */
  const displayedCategories = useMemo(() => {
    if (selectedCategory) {
      return [selectedCategory]
    }

    return allCategories
      .filter(category => category.items.length > 0)
      .filter(category => !RP_TOOLS_IDS.includes(category.id))
      .filter(x => x.items.some(item => item.campaign || item.image))
  }, [allCategories, selectedCategory])

  /* Split categories as the headerCategory is displayed first on top,
     followed by a horizontal Ribbon, followed by otherCategories. */
  const headerCategory = displayedCategories.find(
    x => x.id === ACCOUNT_TOOLS_ID,
  )
  const otherCategories = displayedCategories.filter(
    x => x.id !== ACCOUNT_TOOLS_ID,
  )

  const onSelectCategory = (id: string | undefined) =>
    id
      ? push(CampaignStoreCategory, { categoryId: id })
      : push(CampaignStoreIndex, undefined)

  const getItem = (id: string) => {
    for (const category of allCategories) {
      for (const item of category.items) {
        if (item.id === id) {
          return item
        }
      }
    }

    throw new Error(`item not found: ${id}`)
  }

  const onPreview = (itemId: string | undefined) =>
    itemId ? setStep({ type: Steps.Preview, itemId }) : endStep()

  const onPurchase = (itemId: string) => {
    const item = getItem(itemId)
    const { campaign } = item
    // TS cannot narrow down object properties even if we test
    // them. To maintain type safety we assign the properties we want to
    // narrow (campaign) to variables and pass those variables
    // explicitly when building the final object.
    if (!isAddressValid && !hasValidatedAddress) {
      setStep({ type: Steps.FixAddress, itemId })
      setHasValidatedAddress(true)
    } else if (campaign) {
      // Update (1/20/21): Basic accounts can access/purchase campaigns.
      // account has a plan, allow buying the item
      setStep({
        type: Steps.BuyItem,
        item: {
          ...item,
          campaign,
        },
        itemType: 'campaign',
        plan: { ...account.plan },
      })
    } else {
      // purchase an item
      setStep({
        type: Steps.BuyItem,
        itemType: 'product',
        item: { ...item, campaign },
        plan: account.plan,
      })
    }
  }

  const onSelectPlan = (planId: string, itemId: string) =>
    setStep({
      type: Steps.BuyPlan,
      itemId: itemId,
      planId,
    })

  const isShowingSingleCategory = categoryId !== undefined
  const isScrollBarEnabled =
    action.type !== Steps.Preview && action.type !== Steps.BuyItem

  useAppcues('-L8DaP3XvZmu5XZ88mgo')

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {/* If a category is selected, we show the banner in "category" mode,
             which displays a title and a back button. Otherwise, we show a
             promotion using the hardcoded featuredProducts. TODO: split Banner
             into two different components */}
        {selectedCategory ? (
          <Banner
            isCategorySelected={true}
            title={selectedCategory.name}
            textColor={'white'}
            backgroundStartColor={'#6db0ff'}
            backgroundEndColor={'#7de8f6'}
            onPurchase={product => onPurchase(product.id)}
            onClose={() => onSelectCategory(undefined)}
          />
        ) : (
          featuredProducts.map(product => (
            <Banner
              isCategorySelected={false}
              key={product.id}
              image={product.image ?? undefined}
              title={product.name ?? ''}
              product={product}
              textColor={'white'}
              backgroundStartColor={product.startColor}
              backgroundEndColor={product.endColor}
              onPurchase={() => onPurchase(product.id)}
            />
          ))
        )}
      </div>

      <InstantSearch
        id={'campaign_search_bar'}
        onSearch={setSearchTerm}
        autocomplete={'off'}
      />

      {/* If a search is in progress, show the results. Otherwise show categories */}
      {debouncedSearch ? (
        <SuspenseBoundary
          unresolved={
            <CircularProgress
              style={{
                marginLeft: 'calc(50% - 20px)',
              }}
            />
          }
          failure={DefaultError}
        >
          <SearchResults
            searchTerm={debouncedSearch}
            categoryId={categoryId}
            isScrollBarEnabled={isScrollBarEnabled}
            onPreview={onPreview}
            onPurchase={onPurchase}
          />
        </SuspenseBoundary>
      ) : (
        <>
          {headerCategory && (
            <CampaignList
              key={headerCategory.id}
              category={headerCategory}
              isScrollBarEnabled={isScrollBarEnabled}
              onPreview={onPreview}
              onPurchase={onPurchase}
              isSeeAllButtonEnabled={!isShowingSingleCategory}
              isWrapContentEnabled={isShowingSingleCategory}
              onSelectCategory={onSelectCategory}
            />
          )}

          {otherCategories.length && !selectedCategory ? (
            <TileRibbon
              items={otherCategories}
              isScrollBarEnabled={isScrollBarEnabled}
              title={''}
              isSeeAllButtonEnabled={false}
              isWrapContentEnabled={false}
              onItemCategoryClick={category => onSelectCategory(category.id)}
            />
          ) : null}

          {otherCategories.map(category => (
            <CampaignList
              key={category.id}
              category={category}
              isScrollBarEnabled={isScrollBarEnabled}
              onPreview={onPreview}
              onPurchase={onPurchase}
              isSeeAllButtonEnabled={!isShowingSingleCategory}
              isWrapContentEnabled={isShowingSingleCategory}
              onSelectCategory={onSelectCategory}
            />
          ))}
        </>
      )}

      {/* We're viewing an item, show the modal. */}
      {action.type === Steps.Preview && (
        <SuspenseBoundary unresolved={''} failure={DefaultError}>
          <ItemComponent
            onPurchase={onPurchase}
            item={getItem(action.itemId)}
            onClose={endStep}
          />
        </SuspenseBoundary>
      )}

      {/* User required a plan upgrade, show plan selector */}
      {action.type === Steps.ChoosePlan && (
        <SuspenseBoundary unresolved={''} failure={DefaultError}>
          <FullWindowModal close={endStep}>
            <Plans
              title={upsaleMessage.title}
              message={upsaleMessage.message}
              onSelect={planId => onSelectPlan(planId, action.itemId)}
            />
          </FullWindowModal>
        </SuspenseBoundary>
      )}

      {/* User has picked a plan, show buying modal */}
      {action.type === Steps.BuyPlan && (
        <SuspenseBoundary unresolved={''} failure={DefaultError}>
          <Drawer
            openCloseControlClicked={endStep}
            isOpen={true}
            title={'Purchase Your Subscription Package'}
            isFullScreen={windowWidth < 1060}
          >
            <PlanBuyer
              initialPlanId={action.planId}
              // plan has been bought, re-attempt the purchasing process
              onAfterPurchase={() => onPurchase(action.itemId)}
              onCancel={endStep}
            />
          </Drawer>
        </SuspenseBoundary>
      )}

      {action.type === Steps.BuyItem && (
        <PaymentModal
          account={account}
          item={action.item}
          onCancel={endStep}
          onAfterPurchase={() => {
            setStep({ type: Steps.Thanks })
            // Wait to invalidate GetPaginatedCampaigns after the campaign is added
            setTimeout(() => {
              invalidateQueriesByName('GetPaginatedCampaigns')
            }, 15000)
          }}
        />
      )}

      {action.type === Steps.FixAddress && (
        <ValidateAddress
          isOpen={true}
          onClose={() => {
            endStep()
            setHasValidatedAddress(false)
          }}
          account={account}
          onSubmit={() => onPurchase(action.itemId)}
        />
      )}

      {action.type === Steps.Thanks && <ThanksModal onClose={endStep} />}
    </div>
  )
}

const CampaignStore: React.FC<Props> = props => (
  <SuspenseBoundary
    unresolved={<Transition message={'Gathering campaigns...'} />}
    failure={DefaultError}
  >
    <CampaignStoreContent {...props} />
  </SuspenseBoundary>
)

export default CampaignStore
